import { Boxes, Compass, type LucideIcon, SquareKanban, UploadCloud } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AsciiKMark } from './AsciiKMark'

export interface HeroStat {
	/** Already formatted for display. */
	value: string
	label: string
	to: string
	/** Renders the mockup's red at-risk dot beside the value. */
	alert?: boolean
}

const JUMP_IN: { title: string; desc: string; to: string; icon: LucideIcon }[] = [
	{
		title: 'Work Orders',
		desc: 'Browse the queue and move orders through the workflow.',
		to: '/work-orders',
		icon: SquareKanban,
	},
	{ title: 'Explore', desc: 'Guided funnel to any brand in any market context.', to: '/explore', icon: Compass },
	{ title: 'Library', desc: 'Find any asset by combining dimension facets.', to: '/assets', icon: Boxes },
	{
		title: 'Upload & classify',
		desc: 'Bring assets into the library, classified in bulk.',
		to: '/upload',
		icon: UploadCloud,
	},
]

/**
 * The Home hero — the login's black world carried in: brand orbs, the K mark,
 * a serif greeting, glassy stat tiles, and the Jump-in shortcuts (Atlas ToBe).
 */
export function HomeHero({ greeting, firstName, stats }: { greeting: string; firstName: string; stats: HeroStat[] }) {
	return (
		<div className="relative mb-4 overflow-hidden rounded-ds-card bg-ds-primary px-12 pb-10 pt-11">
			<div className="relative z-1 flex items-center gap-8">
				<AsciiKMark className="hidden h-68 w-101 flex-none lg:block" />
				<div className="min-w-0 flex-1">
					<p className="mb-3.5 font-mono text-ds-3xs uppercase tracking-ds-wider text-ds-background/40">
						Keenfolks X_ · Atlas · Workspace
					</p>
					<h1 className="font-ds-display text-3xl font-normal leading-tight tracking-tight text-ds-background xl:text-4xl">
						{greeting}, <em className="text-ds-background/72">{firstName}.</em>
					</h1>

					<div className="mt-5 flex gap-2">
						{stats.map((stat) => (
							<Link
								key={stat.label}
								to={stat.to}
								className="min-w-0 flex-1 rounded-ds-lg border border-ds-background/14 bg-ds-background/7 px-3.5 py-3 backdrop-blur-sm transition-colors duration-120 hover:bg-ds-background/12"
							>
								<span className="flex items-center gap-1.5 font-ds-display text-2xl tracking-tight text-ds-background">
									{stat.value}
									{stat.alert ? <span className="h-1.5 w-1.5 rounded-ds-full bg-ds-danger" /> : null}
								</span>
								<span className="mt-0.5 block truncate font-mono text-ds-4xs uppercase tracking-ds-wide text-ds-background/45">
									{stat.label}
								</span>
							</Link>
						))}
					</div>

					<p className="mb-2 mt-4.5 font-mono text-ds-3xs uppercase tracking-ds-wider text-ds-background/34">Jump in</p>
					<div className="flex gap-2">
						{JUMP_IN.map(({ title, desc, to, icon: Icon }) => (
							<Link
								key={to}
								to={to}
								title={desc}
								className="flex h-22 min-w-0 flex-1 flex-col justify-between rounded-ds-lg border border-ds-background/13 bg-ds-background/5 px-3 py-2.75 transition-colors duration-120 hover:border-ds-background/30 hover:bg-ds-background/11"
							>
								<Icon className="h-6.5 w-6.5 text-ds-background/62" strokeWidth={1.4} />
								<span className="font-mono text-ds-4xs uppercase leading-ds-tight tracking-ds-wide text-ds-background">
									{title}
								</span>
							</Link>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}
